import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { agentToolCatalog } from "../../autogen/agentToolCatalog";
import { bffApi } from "../../autogen/bff-api";
import type { AgentConversationDto } from "../../autogen/bff-api";
import { useEventNavigation } from "../../pages/sourcing-events/useEventNavigation";
import { urls } from "../../urls";
import { useBaseData } from "../useBaseData";
import { ddRum } from "../../datadog";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import { useApiError } from "../errors/useApiError";
import { useAppDispatch } from "../redux/hooks";
import { createAgentMemory } from "./agentMemory";
import type { WireMessage } from "./agentMemory";
import { streamAgentTurn } from "./agentStream";
import type { StreamHandlers } from "./agentStream";
import type { AgentTurn, ConfirmDecision, DocumentSource } from "./agentTypes";
import { streamAnswer } from "./answerStream";
import { useAssistantPanel } from "./assistantPanelContext";
import { executeCall, resolveEntityNavigation, toolStepLabel } from "./callHandlers";
import { currentToolName } from "./retiredToolNames";
import type { CallDeps } from "./callHandlers";
import { entityLabel } from "./confirmationCardFormatting";
import { resolvePageContext, withPageContext } from "./pageContext";
import { CLIENT_TOOL_SCHEMAS } from "./clientToolSchemas";
import type { PendingConfirmation } from "./ConfirmationCard";
import {
  buildClientState,
  createConversation,
  deleteConversation,
  getConversation,
  parseClientState,
  restoreEntityIndex,
  saveConversation,
  toRestoredTurn,
  updateConversation,
} from "./conversationStore";
import type { ConversationSaveTriggers } from "./conversationStore";
import { toResumableConversation } from "./resumableConversation";
import { buildDraftSystemPrompt, buildSystemPrompt } from "./systemPrompt";
import { toError } from "./tools";
import type { ToolContext, ToolResult } from "./tools";
import { catalogToToolSchemas } from "./toolSchema";
import { runTurnLoop } from "./turnLoop";
import { streamCompletion } from "./streamCompletion";
import { useAgentAudit } from "./useAgentAudit";
import { useAgentSession } from "./useAgentSession";
import { useDocumentSearch } from "./useDocumentSearch";
import { defaultDepartment } from "../departments/defaultDepartment";
import { contractDepartmentOptions, departmentsForNewEvent } from "../departments/eventCreationDepartments";
import { showsContractDepartment } from "../departments/departmentVisibility";

const SAVE_DEBOUNCE_MS = 1_000;

const MAX_STEPS = 6;
const PLAN_TIMEOUT_MS = 60_000;
const ANSWER_TIMEOUT_MS = 90_000;

export const useAssistant = () => {
  const authState = useLoggedInWithOrgContextState();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { panelOpen } = useAssistantPanel();
  const navigateToEvent = useEventNavigation();
  const entityIndex = useRef(new Map<string, Record<string, unknown>>());
  const locationRef = useRef(location);
  locationRef.current = location;

  const organizationId = authState.selectedOrg.id;
  const personId = authState.me.id;
  const departmentId = defaultDepartment(authState.selectedOrg)?.id ?? "";
  const eventDepartments = departmentsForNewEvent(authState.selectedOrg).map((d) => ({ id: d.id, name: d.name }));
  const contractDepartments = showsContractDepartment(authState.selectedOrg)
    ? contractDepartmentOptions(authState.selectedOrg).map((d) => ({ id: d.id, name: d.name }))
    : [];
  const baseData = useBaseData();
  const norwayCountryId = baseData.countries.find((country) => country.alpha3Code === "NOR")?.id ?? "";

  const lookupEntity = useCallback((id: string) => entityIndex.current.get(id), []);

  // Same routing rules the planner's own "navigate" tool call uses (see resolveEntityNavigation),
  // exposed so an entity highlight card can send the user straight to that contract/event on click.
  const navigateToEntity = useCallback(
    (id: string) => resolveEntityNavigation(id, { entityIndex: entityIndex.current, navigate, navigateToEvent })?.(),
    [navigate, navigateToEvent],
  );

  const messagesRef = useRef<WireMessage[]>([]);

  const wireTools = useMemo(
    () =>
      [...catalogToToolSchemas(agentToolCatalog), ...CLIENT_TOOL_SCHEMAS].map((t) => ({
        name: t.name,
        description: t.description,
        parameters: JSON.stringify(t.parameters),
      })),
    [],
  );

  const openDocument = useCallback(
    async (documentId: string, name: string): Promise<{ result: ToolResult; source: DocumentSource | null }> => {
      if (!documentId.trim()) {
        return {
          result: { ok: false, deviationCode: "MISSING_DOCUMENT_ID", message: "Mangler documentId." },
          source: null,
        };
      }
      const subscription = dispatch(
        bffApi.endpoints.getDownloadLink.initiate({ docId: documentId }, { forceRefetch: true }),
      );
      try {
        const { url } = await subscription.unwrap();
        return {
          result: { ok: true, data: { note: "Dokumentet ligger klart som kort under svaret." } },
          source: { documentId, documentName: name.trim() || "Dokument", downloadUrl: url, pages: [] },
        };
      } catch (err) {
        return { result: toError(err), source: null };
      } finally {
        subscription.unsubscribe();
      }
    },
    [dispatch],
  );

  const generateDraft = useCallback(async (args: Record<string, unknown>): Promise<ToolResult> => {
    const kind = String(args.kind ?? "general");
    const instructions = String(args.instructions ?? args.instruction ?? "");
    if (!instructions.trim()) {
      return { ok: false, deviationCode: "MISSING_INSTRUCTION", message: "Mangler instruksjon for utkastet." };
    }
    const messages: WireMessage[] = [
      { role: "system", content: buildDraftSystemPrompt(kind) },
      { role: "user", content: instructions },
    ];
    try {
      const draft = await streamCompletion(messages, selectedModelRef.current ?? undefined);
      return { ok: true, data: { draft } };
    } catch {
      return { ok: false, deviationCode: "DRAFT_FAILED", message: "Klarte ikke å lage utkast." };
    }
  }, []);

  const recordAudit = useAgentAudit();

  const [turns, setTurns] = useState<AgentTurn[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [activeStep, setActiveStep] = useState<string | null>(null);

  // Persistence activates only once the user actually opens the assistant (a panel toggle or the
  // assistant page), so we never fetch conversations for someone who never touches it.
  const [activated, setActivated] = useState(false);
  useEffect(() => {
    if (panelOpen || location.pathname === urls.app.assistant) {
      setActivated(true);
    }
  }, [panelOpen, location.pathname]);

  const listQuery = bffApi.useListAgentConversationsQuery({}, { skip: !activated });
  const modelsQuery = bffApi.useGetAgentModelsQuery({}, { skip: !activated });
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const selectedModelRef = useRef<string | null>(null);
  selectedModelRef.current = selectedModelId;
  const summaries = useMemo(
    () => [...(listQuery.data?.conversations ?? [])].sort((a, b) => b.modifiedAt.localeCompare(a.modifiedAt)),
    [listQuery.data],
  );

  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);

  // Refs mirror the render state so a debounced save (or a save fired just before we clear state on
  // switch/new) reads the values captured at call time, never a stale closure.
  const turnsRef = useRef<AgentTurn[]>([]);
  turnsRef.current = turns;
  const activeIdRef = useRef<string | null>(null);
  activeIdRef.current = activeConversationId;
  // Bumped whenever we swap to another (or a fresh) conversation, so an in-flight save that started
  // under the previous conversation can't adopt its new id back onto the current one.
  const generationRef = useRef(0);

  const saveTriggers = useMemo<ConversationSaveTriggers>(
    () => ({
      create: (title) => createConversation(dispatch, title),
      update: (id, update) => updateConversation(dispatch, id, update),
    }),
    [dispatch],
  );

  // Only one create call may be in flight for a still-id-less conversation at a time — a concurrent
  // persist() (debounced save racing a switchTo/startNew) would otherwise each see activeIdRef as null
  // and both call create(), orphaning a duplicate. The loser just skips its save; last-write-wins
  // means the next settlement (or the winner's own save, already in flight) covers it.
  const isCreatingRef = useRef(false);

  // Save the current conversation's stored memory array exactly as-is (nothing ephemeral leaks in)
  // plus the client-side turn/entity state. Failures are swallowed and logged; the next turn's
  // settlement reschedules a retry. Never blocks the chat.
  const persist = useCallback(async () => {
    const messages = messagesRef.current;
    if (messages.length === 0) {
      return;
    }
    const isFreshCreate = activeIdRef.current === null;
    if (isFreshCreate) {
      if (isCreatingRef.current) {
        return;
      }
      isCreatingRef.current = true;
    }
    const generation = generationRef.current;
    const clientState = buildClientState(turnsRef.current, entityIndex.current, selectedModelRef.current ?? undefined);
    try {
      await saveConversation(
        saveTriggers,
        { activeConversationId: activeIdRef.current, messages, clientState },
        (id) => {
          if (generationRef.current === generation) {
            activeIdRef.current = id;
            setActiveConversationId(id);
          }
        },
      );
    } catch (error) {
      ddRum.addError(error, { source: "assistant-conversation-save" });
    } finally {
      if (isFreshCreate) {
        isCreatingRef.current = false;
      }
    }
  }, [saveTriggers]);

  const applyConversation = useCallback((dto: AgentConversationDto) => {
    generationRef.current += 1;
    const { messages } = toResumableConversation(dto.messages);
    messagesRef.current = messages;
    const clientState = parseClientState(dto.clientState);
    setTurns(clientState.turns.map(toRestoredTurn));
    entityIndex.current = restoreEntityIndex(clientState.entityIndex);
    setSelectedModelId(clientState.model ?? null);
    setActiveConversationId(dto.id);
    activeIdRef.current = dto.id;
  }, []);

  const loadConversation = useCallback(
    async (id: string) => {
      try {
        applyConversation(await getConversation(dispatch, id));
      } catch (error) {
        ddRum.addError(error, { source: "assistant-conversation-load" });
      }
    },
    [applyConversation, dispatch],
  );

  const resetToNewConversation = useCallback(() => {
    generationRef.current += 1;
    messagesRef.current = [];
    entityIndex.current = new Map();
    setTurns([]);
    setSelectedModelId(null);
    setActiveConversationId(null);
    activeIdRef.current = null;
  }, []);

  // Hydrate once, from the newest conversation, the first time the assistant is opened. Bails if the
  // user already started a turn before the initial list+get resolved — loading the newest saved
  // conversation over an in-flight turn would silently clobber messagesRef with no way to recover it.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!activated || hydratedRef.current || !listQuery.isSuccess || isRunning || messagesRef.current.length > 0) {
      return;
    }
    hydratedRef.current = true;
    const newest = summaries[0];
    if (newest) {
      void loadConversation(newest.id);
    }
  }, [activated, listQuery.isSuccess, summaries, loadConversation, isRunning]);

  const switchTo = useCallback(
    async (id: string) => {
      if (isRunning || id === activeIdRef.current) {
        return;
      }
      await persist();
      await loadConversation(id);
    },
    [isRunning, persist, loadConversation],
  );

  const startNew = useCallback(() => {
    if (isRunning) {
      return;
    }
    void persist();
    resetToNewConversation();
  }, [isRunning, persist, resetToNewConversation]);

  const remove = useCallback(
    async (id: string) => {
      if (isRunning) {
        return;
      }
      try {
        await deleteConversation(dispatch, id);
      } catch (error) {
        ddRum.addError(error, { source: "assistant-conversation-delete" });
        return;
      }
      if (id === activeIdRef.current) {
        resetToNewConversation();
      }
    },
    [isRunning, dispatch, resetToNewConversation],
  );

  const [settledAt, setSettledAt] = useState(0);
  useEffect(() => {
    if (settledAt === 0) {
      return;
    }
    const handle = setTimeout(() => void persist(), SAVE_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [settledAt, persist]);

  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);
  const [queuedMessage, setQueuedMessage] = useState<string | null>(null);
  const confirmResolver = useRef<((decision: ConfirmDecision) => void) | null>(null);

  const waitForConfirmation = useCallback((pending: PendingConfirmation): Promise<ConfirmDecision> => {
    setPendingConfirmation(pending);
    setActiveStep(null);
    return new Promise<ConfirmDecision>((resolve) => {
      confirmResolver.current = resolve;
    });
  }, []);

  const settle = useCallback((decision: ConfirmDecision) => {
    setPendingConfirmation(null);
    const resolve = confirmResolver.current;
    confirmResolver.current = null;
    resolve?.(decision);
  }, []);

  const confirm = useCallback(
    (finalArgs: Record<string, unknown>) => settle({ kind: "confirm", finalArgs }),
    [settle],
  );

  const cancel = useCallback(() => settle({ kind: "cancel" }), [settle]);

  const refine = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || !confirmResolver.current) {
        return;
      }
      setQueuedMessage(trimmed);
      settle({ kind: "supersede" });
    },
    [settle],
  );

  const { triggerSoft } = useApiError();

  const currentPageContext = useCallback(
    () =>
      resolvePageContext(locationRef.current.pathname, locationRef.current.search, {
        entityLabel: (id) => entityLabel(entityIndex.current.get(id)) || undefined,
      }),
    [],
  );

  const { createDocSearchSession } = useAgentSession();
  const currentPageEntity = useCallback(() => currentPageContext()?.entity ?? null, [currentPageContext]);
  const searchDocuments = useDocumentSearch({ createDocSearchSession, currentPageEntity });

  const planStream = useCallback(
    (handlers: StreamHandlers) =>
      streamAgentTurn(
        {
          messages: withPageContext(messagesRef.current, currentPageContext()),
          tools: wireTools,
          model: selectedModelRef.current ?? undefined,
        },
        handlers,
        { timeoutMs: PLAN_TIMEOUT_MS },
      ),
    [wireTools, currentPageContext],
  );

  const toolLabel = useCallback(
    (requestedName: string) => {
      const name = currentToolName(requestedName);
      return toolStepLabel(agentToolCatalog.find((tool) => tool.toolName === name)?.description, name);
    },
    [],
  );

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isRunning) {
        return;
      }

      const turnIndex = turns.length;
      const updateTurn = (patch: (turn: AgentTurn) => AgentTurn) =>
        setTurns((prev) => prev.map((turn, i) => (i === turnIndex ? patch(turn) : turn)));

      setTurns((prev) => [...prev, { user: trimmed, steps: [], answerText: null, note: null }]);
      setIsRunning(true);
      setActiveStep(null);
      const onAnswerDelta = (accumulated: string) => {
        setActiveStep(null);
        updateTurn((turn) => ({ ...turn, answerText: accumulated }));
      };

      const ctx: ToolContext = {
        dispatch,
        auth: { organizationId, personId, departmentId, eventDepartments, contractDepartments },
        defaultCountryId: norwayCountryId,
      };
      const callDeps: CallDeps = {
        ctx,
        entityIndex: entityIndex.current,
        navigate,
        navigateToEvent,
        searchDocuments,
        openDocument,
        generateDraft,
        recordAudit,
        waitForConfirmation,
        setActiveStep,
        updateTurn,
        turnIndex,
        userInstruction: trimmed,
        seenCalls: new Set<string>(),
      };

      try {
        const memory = createAgentMemory(messagesRef.current);
        memory.initSystem(buildSystemPrompt({ organizationId, personId, eventDepartments, contractDepartments }));
        memory.pushUser(trimmed);

        const { outcome, plannerReply, toolContext, usedToolNames } = await runTurnLoop({
          planStream,
          executeCall: (name, args, plannerSummary) => executeCall(name, args, callDeps, plannerSummary),
          memory,
          maxSteps: MAX_STEPS,
          setActiveStep,
          onPlannerText: onAnswerDelta,
          onToolCallStart: (name) => setActiveStep(toolLabel(name)),
          onPlanError: (err) => {
            triggerSoft(err);
            updateTurn((turn) => ({ ...turn, note: "Kunne ikke planlegge svaret — prøv igjen." }));
          },
        });

        if (outcome === "answered") {
          updateTurn((turn) => ({ ...turn, answerText: plannerReply }));
        } else if (outcome === "stream") {
          setActiveStep("Skriver svar");
          const answerText = await streamAnswer({
            question: trimmed,
            toolContext,
            usedToolNames,
            plannerReply,
            memory,
            model: selectedModelRef.current ?? undefined,
            timeoutMs: ANSWER_TIMEOUT_MS,
            stream: streamAgentTurn,
            onDelta: onAnswerDelta,
          });
          updateTurn((turn) => ({ ...turn, answerText }));
        }
      } finally {
        setActiveStep(null);
        setIsRunning(false);
        setSettledAt((n) => n + 1);
      }
    },
    [
      isRunning,
      turns,
      planStream,
      toolLabel,
      triggerSoft,
      recordAudit,
      waitForConfirmation,
      dispatch,
      navigate,
      navigateToEvent,
      organizationId,
      personId,
      departmentId,
      searchDocuments,
      generateDraft,
      openDocument,
      norwayCountryId,
    ],
  );

  useEffect(() => {
    if (!isRunning && queuedMessage !== null) {
      const next = queuedMessage;
      setQueuedMessage(null);
      void send(next);
    }
  }, [isRunning, queuedMessage, send]);

  return {
    turns,
    isRunning,
    activeStep,
    pendingConfirmation,
    confirm,
    cancel,
    refine,
    lookupEntity,
    navigateToEntity,
    send,
    summaries,
    activeConversationId,
    switchTo,
    startNew,
    remove,
    models: modelsQuery.data?.models ?? [],
    defaultModelId: modelsQuery.data?.defaultModelId ?? null,
    selectedModelId,
    selectModel: setSelectedModelId,
  };
};
